/**
 * /admin/v1/skills/* API 客户端 · M04 Skill/Tool 管理（S3 Day11）
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

export type SkillClass = "read-only" | "write";
export type RiskLevel = "R0" | "R1" | "R2";
export type HealthStatus = "not_checked" | "ok" | "warning" | "error";

export interface SkillListItem {
	skill_id: string;
	name: string;
	description: string;
	category: string;
	skill_class: SkillClass;
	risk_level: RiskLevel;
	tool_id: string | null;
	requires_dry_run: boolean;
	requires_confirmation: boolean;
	pre_fetchable: boolean;
	pre_fetch_trigger_slots: string[];
	trigger_mode: "any" | "all";
	enabled: boolean;
	health_status: HealthStatus;
	updated_by: string | null;
	updated_at: string | null;
}

export interface SkillsListResponse {
	items: SkillListItem[];
	total: number;
	enabled_count: number;
	disabled_count: number;
}

export interface SkillToggleRequest {
	enabled: boolean;
	updated_by?: string | null;
	reason?: string | null;
}

export interface SkillToggleResponse {
	skill_id: string;
	enabled: boolean;
	updated_by: string | null;
	reason: string | null;
	updated_at: string;
}

export interface SkillPromptResponse {
	skill_id: string;
	source: "default" | "custom";
	planner_mode: string;
	prompt_template: string;
	updated_by: string | null;
	notes: string | null;
	updated_at: string | null;
}

export interface SkillPromptPatchRequest {
	prompt_template: string;
	updated_by?: string | null;
	notes?: string | null;
}

export interface SkillDecisionPreviewRequest {
	merchant_id: string;
	session_id?: string;
	refined_query: string;
	allowed_skills: string[];
	extracted_slots: Record<string, unknown>;
	semantic_task?: string | null;
	semantic_entities?: Record<string, unknown>;
	evidence_needs?: string[];
	selected_playbook_id?: string | null;
	selected_branch?: string | null;
	history_summary?: string;
}

export interface SkillDecisionPreviewResponse {
	skill_id: string;
	planner_mode: string;
	prompt: SkillPromptResponse;
	decision: Record<string, unknown>;
}

export interface SkillSandboxRequest {
	merchant_id: string;
	session_id?: string;
	extracted_slots: Record<string, unknown>;
	rag_namespaces?: string[];
	top_k?: number;
}

export interface SkillSandboxResponse {
	skill_id: string;
	executed: boolean;
	dry_run_only: boolean;
	message: string;
	elapsed_ms: number;
	result: Record<string, unknown> | null;
}

export interface OAuthHealthResponse {
	merchant_id: string;
	shopify_domain: string;
	oauth_state: string;
	scopes_count: number;
	access_token_expires_at: string | null;
	shopify_api_status: HealthStatus;
	checked_live: boolean;
	message: string;
}

export function useSkillsList() {
	return useQuery<SkillsListResponse>({
		queryKey: ["admin", "skills", "list"],
		queryFn: async () => {
			const { data } = await apiClient.get<SkillsListResponse>("/admin/v1/skills/skills");
			return data;
		},
	});
}

export function useToggleSkill(skillId: string) {
	const queryClient = useQueryClient();
	return useMutation<SkillToggleResponse, unknown, SkillToggleRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<SkillToggleResponse>(
				`/admin/v1/skills/skills/${encodeURIComponent(skillId)}/toggle`,
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "skills", "list"] });
		},
	});
}

export function useSkillPrompt(skillId: string) {
	return useQuery<SkillPromptResponse>({
		queryKey: ["admin", "skills", "prompt", skillId],
		queryFn: async () => {
			const { data } = await apiClient.get<SkillPromptResponse>(
				`/admin/v1/skills/skills/${encodeURIComponent(skillId)}/prompt`,
			);
			return data;
		},
		enabled: skillId.length > 0,
	});
}

export function usePatchSkillPrompt(skillId: string) {
	const queryClient = useQueryClient();
	return useMutation<SkillPromptResponse, unknown, SkillPromptPatchRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.patch<SkillPromptResponse>(
				`/admin/v1/skills/skills/${encodeURIComponent(skillId)}/prompt`,
				body,
			);
			return data;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["admin", "skills", "prompt", data.skill_id] });
		},
	});
}

export function useSkillDecisionPreview(skillId: string) {
	return useMutation<SkillDecisionPreviewResponse, unknown, SkillDecisionPreviewRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<SkillDecisionPreviewResponse>(
				`/admin/v1/skills/skills/${encodeURIComponent(skillId)}/decision-preview`,
				body,
			);
			return data;
		},
	});
}

export function useSkillSandbox(skillId: string) {
	return useMutation<SkillSandboxResponse, unknown, SkillSandboxRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<SkillSandboxResponse>(
				`/admin/v1/skills/skills/${encodeURIComponent(skillId)}/sandbox`,
				body,
			);
			return data;
		},
	});
}

export function useOAuthHealthCheck() {
	return useMutation<OAuthHealthResponse, unknown, { merchant_id: string; probe?: boolean }>({
		mutationFn: async ({ merchant_id, probe = false }) => {
			const { data } = await apiClient.get<OAuthHealthResponse>("/admin/v1/skills/oauth-health", {
				params: { merchant_id, probe },
			});
			return data;
		},
	});
}

/**
 * 接入状态页用：自动发起带 live probe 的 Shopify 连通性探测。
 * 默认 probe=true，让"接入状态"反映真实 token 连通，而非硬编码绿灯。
 */
export function useOAuthHealth(merchantId: string, probe = true) {
	return useQuery<OAuthHealthResponse>({
		queryKey: ["admin", "skills", "oauth-health", merchantId, probe],
		queryFn: async () => {
			const { data } = await apiClient.get<OAuthHealthResponse>("/admin/v1/skills/oauth-health", {
				params: { merchant_id: merchantId, probe },
			});
			return data;
		},
		enabled: merchantId.length > 0,
		staleTime: 30_000,
		retry: false,
	});
}
